/**
 * InfoSidebar - Off-canvas contextual help panel.
 *
 * Desktop: Right-side slide-in panel (~400px).
 * Mobile:  Bottom sheet, full-width, max-h 80vh.
 */

import { useEffect, useCallback } from 'react';

export interface InfoSection {
  heading: string;
  body: string;
}

export interface InfoSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  sections: InfoSection[];
}

const InfoSidebar = ({ isOpen, onClose, title, description, sections }: InfoSidebarProps) => {
  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Desktop panel (right slide-in) */}
      <aside
        className="hidden md:flex fixed top-0 right-0 h-full w-[400px] max-w-[90vw] z-[61]
                   flex-col bg-[rgba(15,23,42,0.95)] backdrop-blur-[20px] border-l border-white/5
                   shadow-card-lg animate-slide-in-right"
        role="complementary"
        aria-label="Page information"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <div>
            <h2 className="text-lg font-bold font-display text-white">{title}</h2>
            {description && (
              <p className="text-sm text-text-muted mt-1">{description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors text-text-muted hover:text-white"
            aria-label="Close info panel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {sections.map((section, i) => (
            <div key={i}>
              <h3 className="text-sm font-bold font-display text-accent uppercase tracking-wide mb-2">
                {section.heading}
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/5 text-center">
          <span className="text-xs text-text-muted">Press <kbd className="px-1.5 py-0.5 rounded bg-white/5 text-text-secondary text-xs">Esc</kbd> to close</span>
        </div>
      </aside>

      {/* Mobile panel (bottom sheet) */}
      <aside
        className="md:hidden fixed bottom-0 left-0 right-0 z-[61] max-h-[80vh]
                   flex flex-col bg-[rgba(15,23,42,0.95)] backdrop-blur-[20px]
                   border-t border-white/5 rounded-t-3xl shadow-card-lg animate-slide-up-mobile"
        role="complementary"
        aria-label="Page information"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-white/5">
          <div>
            <h2 className="text-lg font-bold font-display text-white">{title}</h2>
            {description && (
              <p className="text-sm text-text-muted mt-1">{description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors text-text-muted hover:text-white"
            aria-label="Close info panel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {sections.map((section, i) => (
            <div key={i}>
              <h3 className="text-sm font-bold font-display text-accent uppercase tracking-wide mb-2">
                {section.heading}
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default InfoSidebar;
